import { useMutation } from '@vue/apollo-composable';

import type { School } from '@/types/composition-functions';
import {
  UPLOAD_SCHOOL_AFFILIATE_LOGO,
  REMOVE_SCHOOL_AFFILIATE_LOGO,
} from '@/graphql/mutations';

/** Max affiliate-logo size, mirroring the school-logo uploader constraint. */
export const MAX_AFFILIATE_LOGO_BYTES = 2 * 1024 * 1024;

type AffiliateSchoolFields = Pick<School, 'id' | 'affiliate_logo' | 'affiliate_name'>;

/**
 * Wraps the partner / affiliate logo mutations. These have no generated
 * vue-apollo hook yet, so we drive them through the raw `useMutation`
 * composable (as several other composables in this repo do). The affiliate
 * *name* is saved through the existing `updateSchool` mutation by the caller;
 * this composable owns only the logo upload + removal.
 *
 * Both mutations select the affiliate fields back so Apollo's cache normalises
 * them onto the cached `School` entity; `GetSchool` is also refetched so any
 * other consumer of the school data stays in sync.
 */
export function useAffiliateBranding() {
  const { mutate: uploadMutation, loading: uploading } = useMutation<
    { uploadSchoolAffiliateLogo: AffiliateSchoolFields },
    { logo: File }
  >(UPLOAD_SCHOOL_AFFILIATE_LOGO, { refetchQueries: ['GetSchool'] });

  const { mutate: removeMutation, loading: removing } = useMutation<{
    removeSchoolAffiliateLogo: AffiliateSchoolFields;
  }>(REMOVE_SCHOOL_AFFILIATE_LOGO, { refetchQueries: ['GetSchool'] });

  /** Returns a validation message when the file is invalid, otherwise null. */
  function validateAffiliateLogo(file: File): string | null {
    if (!file.type.startsWith('image/')) {
      return 'Affiliate logo must be an image file.';
    }
    if (file.size > MAX_AFFILIATE_LOGO_BYTES) {
      return 'Affiliate logo must be under 2MB.';
    }
    return null;
  }

  function uploadAffiliateLogo(file: File) {
    return uploadMutation({ logo: file });
  }

  function removeAffiliateLogo() {
    return removeMutation();
  }

  return {
    uploading,
    removing,
    validateAffiliateLogo,
    uploadAffiliateLogo,
    removeAffiliateLogo,
  };
}
